import { phrase } from '../messages/phrase.js';
import { ringDistance } from '../world/grid.js';
/**
 * Zone bosses (ADR 0024, ADR 0029), as pure rules: where an ability lands, who it
 * catches, how hard, which thresholds a blow crosses, who shares the kill.
 * The server owns the clock and the state (`BossDirector`); nothing here
 * keeps any.
 */
/** A participant this far from the corpse, or further, walked away from the fight. */
export const BOSS_SHARE_RANGE = 20;
/**
 * What a victor earns past their day's rewarded kill of a boss (ADR 0090):
 * a tenth of its experience and no loot. A boss back every minute is there
 * to be retried and to help a friend, not to be farmed.
 */
export const BOSS_REPEAT_XP_SHARE = 0.1;
/** The experience a boss kill pays: whole on the day's rewarded kill, a tenth after. */
export function bossKillXp(xp, rewarded) {
    if (rewarded || xp <= 0)
        return xp;
    return Math.max(1, Math.round(xp * BOSS_REPEAT_XP_SHARE));
}
/** Whether `tile` lies in the disc of `radius` around `centre` (Chebyshev, like melee). */
export function inDisc(centre, radius, tile) {
    return ringDistance(centre, tile) <= radius;
}
/** Where an ability is aimed from: a slam on its target's tile, every other shape on the boss. */
export function abilityCentre(ability, boss, target) {
    return ability.kind === 'slam' ? { ...target } : { ...boss };
}
/**
 * The tiles an ability covers (ADR 0029), exactly those the client marks and
 * the server checks when it lands: open ground only, each tile once.
 */
export function abilityTiles(ability, boss, target, ground) {
    const tiles = new Map();
    const add = (tile) => {
        if (ground.open(tile))
            tiles.set(`${String(tile.x)},${String(tile.y)}`, tile);
    };
    const square = (centre, radius, inner = -1) => {
        for (let dy = -radius; dy <= radius; dy += 1) {
            for (let dx = -radius; dx <= radius; dx += 1) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) <= inner)
                    continue;
                add({ x: centre.x + dx, y: centre.y + dy });
            }
        }
    };
    // Along an axis from the boss: the axis tile and `radius` either side,
    // until the axis meets a wall.
    const arm = (dx, dy, length) => {
        const side = { x: -dy, y: dx };
        for (let step = 1; step <= length; step += 1) {
            const axis = { x: boss.x + dx * step, y: boss.y + dy * step };
            if (!ground.open(axis))
                break;
            for (let k = -ability.radius; k <= ability.radius; k += 1) {
                add({ x: axis.x + side.x * k, y: axis.y + side.y * k });
            }
        }
    };
    switch (ability.kind) {
        case 'slam':
            square(target, ability.radius);
            break;
        case 'nova':
            square(boss, ability.radius);
            break;
        case 'ring':
            square(boss, ability.radius, ability.inner ?? 0);
            break;
        case 'line': {
            // Toward the target, straight or on a diagonal: the eight ways a mob walks.
            const dx = Math.sign(target.x - boss.x);
            const dy = Math.sign(target.y - boss.y);
            arm(dx === 0 && dy === 0 ? 0 : dx, dx === 0 && dy === 0 ? 1 : dy, ability.length ?? 1);
            break;
        }
        case 'cross':
            add(boss);
            for (const [dx, dy] of [
                [1, 0],
                [-1, 0],
                [0, 1],
                [0, -1],
            ]) {
                arm(dx, dy, ability.length ?? 1);
            }
            break;
        case 'rain': {
            const spread = ability.spread ?? 1;
            const count = ability.count ?? 1;
            const drops = ground.players
                .filter((p) => ringDistance(p, boss) <= spread)
                .slice(0, count)
                .map((p) => ({ ...p }));
            for (let attempt = 0; drops.length < count && attempt < count * 8; attempt += 1) {
                const drop = {
                    x: boss.x + Math.round((ground.rng() * 2 - 1) * spread),
                    y: boss.y + Math.round((ground.rng() * 2 - 1) * spread),
                };
                // Drops apart from one another, so the rain is a pattern, not a blanket.
                if (drops.some((d) => ringDistance(d, drop) <= ability.radius * 2))
                    continue;
                drops.push(drop);
            }
            for (const drop of drops)
                square(drop, ability.radius);
            break;
        }
    }
    return [...tiles.values()];
}
/** Whether a tile is one of an ability's. */
export function inTiles(tiles, tile) {
    return tiles.some((t) => t.x === tile.x && t.y === tile.y);
}
/**
 * The first ability ready at `now`, in the order the boss lists them, or
 * undefined. `readyAt[i]` is when ability `i` may next be used.
 */
export function readyAbility(readyAt, now) {
    const index = readyAt.findIndex((at) => now >= at);
    return index === -1 ? undefined : index;
}
/**
 * Share of a player's greatest health one `multiplier` of a zone boss's
 * ability takes (ADR 0088): a scythe of 2.5 takes 45 %, a frost wave of 3
 * 54 %, two big ones in a row and a bite kill. Measured through defence, a
 * player in +3 armour stood in them for a few points; a mark on the ground
 * is there to be dodged, not tanked, like Shefira's in a raid (ADR 0068 §3).
 */
export const BOSS_SHAPE_SHARE_PER_MULTIPLIER = 0.18;
/** The share of greatest health an ability of this `multiplier` takes, whatever the armour. */
export function abilityShare(ability) {
    return Math.min(1, Math.max(0, ability.multiplier * BOSS_SHAPE_SHARE_PER_MULTIPLIER));
}
/**
 * What an ability deals to one player caught in it, of `maxHealth`: its
 * share of that health, at least a point. Defence does not soften it; only
 * a step aside (or the shield wall, applied by the room) does.
 */
export function abilityDamage(ability, maxHealth) {
    if (ability.multiplier <= 0 || maxHealth <= 0)
        return 0;
    return Math.max(1, Math.round(abilityShare(ability) * maxHealth));
}
/**
 * The thresholds a blow carried the boss across, from `before` to `after`
 * health out of `maxHp`, leaving out those already `fired` this life. A blow
 * that takes it from 60 % to 20 % crosses both, in order.
 */
export function phasesCrossed(boss, before, after, maxHp, fired) {
    if (maxHp <= 0 || after <= 0)
        return [];
    const crossed = [];
    const thresholds = [
        ['summon', boss.summon.at],
        ['enrage', boss.enrage.at],
    ];
    for (const [phase, share] of thresholds) {
        if (fired.has(phase))
            continue;
        const line = maxHp * share;
        if (before > line && after <= line)
            crossed.push(phase);
    }
    return crossed;
}
/**
 * Who shares a kill: every character that struck the boss this life and
 * still stands within `BOSS_SHARE_RANGE` of the corpse. `positions` holds the
 * characters present in the zone, alive or not; one missing has left.
 */
export function bossShares(strikers, positions, corpse) {
    const shares = [];
    for (const id of strikers) {
        const at = positions.get(id);
        if (at !== undefined && ringDistance(at, corpse) < BOSS_SHARE_RANGE)
            shares.push(id);
    }
    return shares;
}
/** Back in its arena: told to the players of its dungeon only, it wakes every minute (ADR 0090). */
export function bossAwake(bossName, lair) {
    return phrase("{boss} s'est éveillé : {lair}", { boss: bossName, lair });
}
/** The victors by name, the last two joined by « et ». */
export function bossSlain(bossName, victors) {
    if (victors.length === 0)
        return phrase('{boss} est tombé', { boss: bossName });
    if (victors.length === 1) {
        return phrase('{boss} a été vaincu par {player}', { boss: bossName, player: victors[0] ?? '' });
    }
    return phrase('{boss} a été vaincu par {names} et {last}', {
        boss: bossName,
        names: victors.slice(0, -1).join(', '),
        last: victors.at(-1) ?? '',
    });
}
//# sourceMappingURL=boss.js.map